import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Popup from "../../../components/Popup";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinancePanel,
    FinanceHeadCell,
    FinanceTableRow,
    FinanceStatTile,
    financeTableContainerSx,
} from "./financeUi";
import {
    getFeeSummary,
    getCollectionReport,
    getClassFeeReport,
    getFeeAuditLogs,
    bulkAssignFees,
} from "../../../redux/feeRelated/feeHandle";
import { underFeeControl } from "../../../redux/feeRelated/feeSlice";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { getSchoolIdForUser } from "../../../utils/userContext";

const TERMS = ["Term 1", "Term 2", "Term 3", "Full Year"];
const y = new Date().getFullYear();
const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];

function csvEscape(val) {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function downloadCsv(filename, rows) {
    const csv = rows.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

const FinanceReports = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const {
        feeSummary,
        collectionReport,
        classFeeReport,
        feeAuditLogs,
        loading,
        statestatus,
        response,
        error,
    } = useSelector((s) => s.fee);
    const { sclassesList } = useSelector((s) => s.sclass);

    const schoolId = getSchoolIdForUser(currentUser);

    const [tab, setTab] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [sumTerm, setSumTerm] = useState("");
    const [sumYear, setSumYear] = useState("");

    const [colFrom, setColFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [colTo, setColTo] = useState(() => new Date().toISOString().slice(0, 10));
    const [colGroupBy, setColGroupBy] = useState("day");

    const [classId, setClassId] = useState("");

    const [bulkText, setBulkText] = useState("");
    const [bulkOutcome, setBulkOutcome] = useState(null);

    const loadSummary = useCallback(() => {
        if (!schoolId) return;
        const filters = {};
        if (sumTerm) filters.term = sumTerm;
        if (sumYear) filters.academicYear = sumYear;
        dispatch(getFeeSummary(schoolId, filters));
    }, [dispatch, schoolId, sumTerm, sumYear]);

    const loadCollections = useCallback(() => {
        if (!schoolId || !colFrom || !colTo) return;
        dispatch(
            getCollectionReport(schoolId, {
                from: colFrom,
                to: colTo,
                groupBy: colGroupBy,
            })
        );
    }, [dispatch, schoolId, colFrom, colTo, colGroupBy]);

    const loadClassReport = useCallback(() => {
        if (!classId) return;
        dispatch(getClassFeeReport(classId));
    }, [dispatch, classId]);

    const loadAudit = useCallback(() => {
        if (!schoolId) return;
        dispatch(getFeeAuditLogs(schoolId, { limit: "200", skip: "0" }));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (!schoolId) return;
        dispatch(getAllSclasses(schoolId, "Sclass"));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (tab === 3 && schoolId) loadAudit();
    }, [tab, schoolId, loadAudit]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            dispatch(underFeeControl());
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setShowPopup(true);
            dispatch(underFeeControl());
        }
    }, [statestatus, response, error, dispatch]);

    const payments = useMemo(
        () => (Array.isArray(collectionReport?.payments) ? collectionReport.payments : []),
        [collectionReport]
    );
    const classRecords = useMemo(
        () => (Array.isArray(classFeeReport?.records) ? classFeeReport.records : []),
        [classFeeReport]
    );
    const auditRows = useMemo(() => (Array.isArray(feeAuditLogs) ? feeAuditLogs : []), [feeAuditLogs]);

    const exportSummaryCsv = () => {
        if (!feeSummary) return;
        const rows = [
            [
                "metric",
                "value",
            ].join(","),
            ["totalExpected", csvEscape(feeSummary.totalExpected)].join(","),
            ["totalCollected", csvEscape(feeSummary.totalCollected)].join(","),
            ["totalOutstanding", csvEscape(feeSummary.totalOutstanding)].join(","),
            ["totalWaived", csvEscape(feeSummary.totalWaived)].join(","),
            ["collectionRate", csvEscape(feeSummary.collectionRate)].join(","),
            ["todayCollection", csvEscape(feeSummary.todayCollection)].join(","),
            ["recordCount", csvEscape(feeSummary.recordCount)].join(","),
        ];
        if (feeSummary.byStatus && typeof feeSummary.byStatus === "object") {
            for (const [k, v] of Object.entries(feeSummary.byStatus)) {
                rows.push(["byStatus_" + k, csvEscape(v)].join(","));
            }
        }
        downloadCsv(`fee-summary-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    };

    const exportCollectionsCsv = () => {
        const headers = [
            "receiptNumber",
            "amount",
            "paymentMethod",
            "paymentDate",
            "studentName",
            "rollNum",
            "class",
            "term",
            "academicYear",
        ];
        const body = payments.map((p) =>
            [
                csvEscape(p.receiptNumber),
                csvEscape(p.amount),
                csvEscape(p.paymentMethod),
                csvEscape(p.paymentDate),
                csvEscape(p.studentName),
                csvEscape(p.rollNum),
                csvEscape(p.sclassName),
                csvEscape(p.term),
                csvEscape(p.academicYear),
            ].join(",")
        );
        downloadCsv(
            `fee-collections-${colFrom}_to_${colTo}.csv`,
            [headers.join(","), ...body]
        );
    };

    const exportGroupedCsv = () => {
        const g = collectionReport?.grouped;
        if (!g || typeof g !== "object") return;
        const rows = [["period", "amount"].join(","), ...Object.keys(g).map((k) => [csvEscape(k), csvEscape(g[k])].join(","))];
        downloadCsv(`fee-collections-grouped-${colGroupBy}.csv`, rows);
    };

    const exportClassCsv = () => {
        const headers = [
            "student",
            "rollNum",
            "totalDue",
            "totalPaid",
            "balance",
            "status",
            "term",
            "academicYear",
        ];
        const body = classRecords.map((r) =>
            [
                csvEscape(r.student?.name),
                csvEscape(r.student?.rollNum),
                csvEscape(r.totalDue),
                csvEscape(r.totalPaid),
                csvEscape(r.balance),
                csvEscape(r.status),
                csvEscape(r.term),
                csvEscape(r.academicYear),
            ].join(",")
        );
        downloadCsv(`fee-class-report-${classId.slice(0, 8)}.csv`, [headers.join(","), ...body]);
    };

    const exportAuditCsv = () => {
        const headers = ["createdAt", "action", "entityType", "entityId", "actorId", "details"];
        const body = auditRows.map((r) =>
            [
                csvEscape(r.createdAt),
                csvEscape(r.action),
                csvEscape(r.entityType),
                csvEscape(r.entityId),
                csvEscape(r.actorId),
                csvEscape(typeof r.details === "object" ? JSON.stringify(r.details) : r.details),
            ].join(",")
        );
        downloadCsv(`fee-audit-${new Date().toISOString().slice(0, 10)}.csv`, [headers.join(","), ...body]);
    };

    const parseBulkAssignments = (text) => {
        const t = text.trim();
        if (!t) return [];
        if (t.startsWith("[")) {
            const arr = JSON.parse(t);
            if (!Array.isArray(arr)) throw new Error("JSON must be an array");
            return arr.map((row) => ({
                studentId: String(row.studentId || row.student_id || "").trim(),
                feeStructureId: String(row.feeStructureId || row.fee_structure_id || "").trim(),
            }));
        }
        const lines = t.split(/\r?\n/).filter((l) => l.trim());
        return lines.map((line, index) => {
            const parts = line.split(/[\t,;]/).map((x) => x.trim());
            if (parts.length < 2) {
                throw new Error(`Line ${index + 1}: need two columns (studentId, feeStructureId)`);
            }
            return { studentId: parts[0], feeStructureId: parts[1] };
        });
    };

    const submitBulk = async () => {
        setBulkOutcome(null);
        if (!schoolId) return;
        let assignments;
        try {
            assignments = parseBulkAssignments(bulkText);
        } catch (e) {
            setMessage(e.message || "Invalid bulk data");
            setShowPopup(true);
            return;
        }
        if (assignments.length === 0) {
            setMessage("Paste at least one row or a JSON array.");
            setShowPopup(true);
            return;
        }
        const out = await dispatch(
            bulkAssignFees({
                schoolId,
                assignments,
                actorId: currentUser?._id,
            })
        );
        if (out) setBulkOutcome(out);
    };

    return (
        <FinancePageContainer>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <FinancePageHeader
                title="Reports & operations"
                subtitle="Summaries, collection trends, class breakdown, audit trail, and bulk fee assignment in one place."
            />

            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: (theme) => theme.shadows[1],
                    overflow: "hidden",
                }}
            >
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: 1,
                        bgcolor: (theme) => theme.palette.grey[50],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
                    }}
                >
                    <Tab label="Fee summary" />
                    <Tab label="Collections" />
                    <Tab label="Class report" />
                    <Tab label="Audit log" />
                    <Tab label="Bulk assign" />
                </Tabs>
            </Paper>

            {tab === 0 && (
                <FinancePanel
                    title="Fee summary"
                    description="Optional term and year filters, then load. Export a single CSV of headline metrics."
                    sx={{ mb: 2 }}
                    contentSx={{ p: { xs: 2, sm: 2.5 } }}
                >
                    <Stack spacing={2}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Term</InputLabel>
                                    <Select
                                        label="Term"
                                        value={sumTerm}
                                        onChange={(e) => setSumTerm(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>All terms</em>
                                        </MenuItem>
                                        {TERMS.map((t) => (
                                            <MenuItem key={t} value={t}>
                                                {t}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Academic year</InputLabel>
                                    <Select
                                        label="Academic year"
                                        value={sumYear}
                                        onChange={(e) => setSumYear(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>All years</em>
                                        </MenuItem>
                                        {ACADEMIC_YEARS.map((yr) => (
                                            <MenuItem key={yr} value={yr}>
                                                {yr}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Button variant="contained" onClick={loadSummary} disabled={!schoolId || loading}>
                                    Load
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4} md={3}>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={exportSummaryCsv}
                                    disabled={!feeSummary}
                                >
                                    Export CSV
                                </Button>
                            </Grid>
                        </Grid>
                        {feeSummary && (
                            <Grid container spacing={2}>
                                {[
                                    ["Total expected", feeSummary.totalExpected, "text.primary"],
                                    ["Total collected", feeSummary.totalCollected, "success.main"],
                                    ["Outstanding", feeSummary.totalOutstanding, "error.main"],
                                    ["Waived", feeSummary.totalWaived, "info.main"],
                                    ["Collection rate", feeSummary.collectionRate, "primary.main"],
                                    ["Today", feeSummary.todayCollection, "text.primary"],
                                    ["Records", feeSummary.recordCount, "text.primary"],
                                ].map(([label, val, vc]) => (
                                    <Grid item xs={6} sm={6} md={4} key={label}>
                                        <FinanceStatTile label={label} value={String(val ?? "—")} valueColor={vc} />
                                    </Grid>
                                ))}
                                {feeSummary.byStatus && (
                                    <Grid item xs={12}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                By status
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                {Object.entries(feeSummary.byStatus)
                                                    .map(([k, v]) => `${k}: ${v}`)
                                                    .join(" · ")}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Stack>
                </FinancePanel>
            )}

            {tab === 1 && (
                <FinancePanel
                    title="Collections report"
                    description="Requires from and to dates. Use grouped CSV for totals by day or month."
                    sx={{ mb: 2 }}
                    contentSx={{ p: { xs: 2, sm: 2.5 } }}
                >
                    <Stack spacing={2}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="From"
                                    type="date"
                                    value={colFrom}
                                    onChange={(e) => setColFrom(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="To"
                                    type="date"
                                    value={colTo}
                                    onChange={(e) => setColTo(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Group by</InputLabel>
                                    <Select
                                        label="Group by"
                                        value={colGroupBy}
                                        onChange={(e) => setColGroupBy(e.target.value)}
                                    >
                                        <MenuItem value="day">Day</MenuItem>
                                        <MenuItem value="month">Month</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Button variant="contained" onClick={loadCollections} disabled={!schoolId || loading}>
                                    Load
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={exportCollectionsCsv}
                                        disabled={payments.length === 0}
                                    >
                                        Payments CSV
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={exportGroupedCsv}
                                        disabled={!collectionReport?.grouped}
                                    >
                                        Grouped CSV
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                        {collectionReport && (
                            <Stack spacing={1}>
                                <Typography variant="body2">
                                    Total collected: <strong>{collectionReport.totalCollected}</strong> · Count:{" "}
                                    <strong>{collectionReport.count}</strong>
                                </Typography>
                                {collectionReport.byMethod && (
                                    <Typography variant="body2" color="text.secondary">
                                        By method:{" "}
                                        {Object.entries(collectionReport.byMethod)
                                            .map(([m, a]) => `${m}: ${a}`)
                                            .join(" · ")}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                        <TableContainer sx={financeTableContainerSx}>
                            <Table size="medium">
                                <TableHead>
                                    <TableRow>
                                        <FinanceHeadCell>Date</FinanceHeadCell>
                                        <FinanceHeadCell>Receipt</FinanceHeadCell>
                                        <FinanceHeadCell>Student</FinanceHeadCell>
                                        <FinanceHeadCell>Class</FinanceHeadCell>
                                        <FinanceHeadCell align="right">Amount</FinanceHeadCell>
                                        <FinanceHeadCell>Method</FinanceHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <FinanceTableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    {loading ? "Loading…" : "Load a date range to see payments."}
                                                </Typography>
                                            </TableCell>
                                        </FinanceTableRow>
                                    ) : (
                                        payments.map((p) => {
                                            const pid = p.id || p._id || p.receiptNumber;
                                            return (
                                                <FinanceTableRow key={pid}>
                                                    <TableCell>{String(p.paymentDate ?? "").slice(0, 10)}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{p.receiptNumber}</TableCell>
                                                    <TableCell>{p.studentName}</TableCell>
                                                    <TableCell>{p.sclassName}</TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                        {p.amount}
                                                    </TableCell>
                                                    <TableCell>{p.paymentMethod}</TableCell>
                                                </FinanceTableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </FinancePanel>
            )}

            {tab === 2 && (
                <FinancePanel
                    title="Class fee report"
                    description="Pick a class, load, then export CSV for offline analysis."
                    sx={{ mb: 2 }}
                    contentSx={{ p: { xs: 2, sm: 2.5 } }}
                >
                    <Stack spacing={2}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Class</InputLabel>
                                    <Select
                                        label="Class"
                                        value={classId}
                                        onChange={(e) => setClassId(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Select class</em>
                                        </MenuItem>
                                        {(Array.isArray(sclassesList) ? sclassesList : []).map((c) => {
                                            const cid = c._id || c.id;
                                            return (
                                                <MenuItem key={cid} value={cid}>
                                                    {c.sclassName}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button variant="contained" onClick={loadClassReport} disabled={!classId || loading}>
                                    Load
                                </Button>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={exportClassCsv}
                                    disabled={classRecords.length === 0}
                                >
                                    Export CSV
                                </Button>
                            </Grid>
                        </Grid>
                        {classFeeReport?.summary && (
                            <Typography variant="body2">
                                Students: {classFeeReport.summary.totalStudents} · Due:{" "}
                                {classFeeReport.summary.totalDue} · Paid: {classFeeReport.summary.totalPaid} · Balance:{" "}
                                {classFeeReport.summary.totalBalance}
                            </Typography>
                        )}
                        <TableContainer sx={financeTableContainerSx}>
                            <Table size="medium">
                                <TableHead>
                                    <TableRow>
                                        <FinanceHeadCell>Student</FinanceHeadCell>
                                        <FinanceHeadCell>Roll</FinanceHeadCell>
                                        <FinanceHeadCell align="right">Due</FinanceHeadCell>
                                        <FinanceHeadCell align="right">Paid</FinanceHeadCell>
                                        <FinanceHeadCell align="right">Balance</FinanceHeadCell>
                                        <FinanceHeadCell>Status</FinanceHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {classRecords.length === 0 ? (
                                        <FinanceTableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">
                                                    Choose a class and load.
                                                </Typography>
                                            </TableCell>
                                        </FinanceTableRow>
                                    ) : (
                                        classRecords.map((r) => {
                                            const rid = r._id || r.id;
                                            return (
                                                <FinanceTableRow key={rid}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{r.student?.name}</TableCell>
                                                    <TableCell>{r.student?.rollNum}</TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                        {r.totalDue}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                        {r.totalPaid}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                                        {r.balance}
                                                    </TableCell>
                                                    <TableCell>{r.status}</TableCell>
                                                </FinanceTableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </FinancePanel>
            )}

            {tab === 3 && (
                <FinancePanel
                    title="Fee audit log"
                    description="Structure changes, payments, voids, waivers, assignments, reminders, and bulk runs."
                    action={
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" size="small" onClick={loadAudit} disabled={!schoolId || loading}>
                                Refresh
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={exportAuditCsv}
                                disabled={auditRows.length === 0}
                            >
                                Export CSV
                            </Button>
                        </Stack>
                    }
                    sx={{ mb: 2 }}
                    contentSx={{ p: 0 }}
                >
                    <TableContainer sx={financeTableContainerSx}>
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <FinanceHeadCell>When</FinanceHeadCell>
                                    <FinanceHeadCell>Action</FinanceHeadCell>
                                    <FinanceHeadCell>Entity</FinanceHeadCell>
                                    <FinanceHeadCell>Details</FinanceHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditRows.length === 0 ? (
                                    <FinanceTableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography color="text.secondary">
                                                {loading ? "Loading…" : "No entries yet. Refresh after fee activity."}
                                            </Typography>
                                        </TableCell>
                                    </FinanceTableRow>
                                ) : (
                                    auditRows.map((r) => {
                                        const rid = r._id || r.id;
                                        const det =
                                            typeof r.details === "object"
                                                ? JSON.stringify(r.details)
                                                : String(r.details ?? "");
                                        return (
                                            <FinanceTableRow key={rid}>
                                                <TableCell sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                                                    {String(r.createdAt ?? "").replace("T", " ").slice(0, 19)}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>{r.action}</TableCell>
                                                <TableCell>
                                                    {r.entityType}{" "}
                                                    {r.entityId ? `· ${r.entityId.slice(0, 8)}…` : ""}
                                                </TableCell>
                                                <TableCell
                                                    sx={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis" }}
                                                    title={det}
                                                >
                                                    {det}
                                                </TableCell>
                                            </FinanceTableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </FinancePanel>
            )}

            {tab === 4 && (
                <FinancePanel
                    title="Bulk assign fee structures"
                    description="Paste rows (studentId, feeStructureId) or a JSON array. Max 500 rows per request."
                    sx={{ mb: 2 }}
                    contentSx={{ p: { xs: 2, sm: 2.5 } }}
                >
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            One pair per line: <code>studentId</code>, then tab or comma, then <code>feeStructureId</code>.
                            Or paste a JSON array:{" "}
                            <code>[{`{"studentId":"...","feeStructureId":"..."}`}]</code>
                        </Typography>
                        <TextField
                            multiline
                            minRows={8}
                            fullWidth
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            placeholder={"studentId1,feeStructureId1\nstudentId2,feeStructureId2"}
                        />
                        <Button variant="contained" onClick={submitBulk} disabled={!schoolId || loading}>
                            Run bulk assign (max 500 rows)
                        </Button>
                        {bulkOutcome && (
                            <Alert severity={bulkOutcome.errors?.length ? "warning" : "success"}>
                                Created: {bulkOutcome.created} · Skipped (already assigned): {bulkOutcome.skipped}
                                {bulkOutcome.errors?.length > 0 && (
                                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                                        {bulkOutcome.errors.slice(0, 20).map((e, i) => (
                                            <li key={i}>
                                                Row {e.index + 1}: {e.message}
                                            </li>
                                        ))}
                                        {bulkOutcome.errors.length > 20 && (
                                            <li>…and {bulkOutcome.errors.length - 20} more</li>
                                        )}
                                    </Box>
                                )}
                            </Alert>
                        )}
                    </Stack>
                </FinancePanel>
            )}
        </FinancePageContainer>
    );
};

export default FinanceReports;
